/** Resolves {{...}} expressions in a text string using the given promptVariables, then evaluates math via EvaluateMathExpressionSafely. */
export function ResolvePromptVariables(text: string, promptVariables: Array<{name: string, value: string}>): string {
	return text.replace(/\{\{([\s\S]*?)\}\}/g, (_match, expr: string)=>{
		// 1. Sort variables by longest name first to avoid partial-name conflicts
		const sortedVars = [...promptVariables].filter(v=>v.name.length > 0).OrderByDescending(a=>a.name.length);
		// Apply substitutions without re-processing substituted values (use placeholder pass)
		let resolvedExpr = expr;
		const placeholder = (i: number)=>`\x00VAR${i}\x00`;
		for (let i = 0; i < sortedVars.length; i++) {
			resolvedExpr = resolvedExpr.replaceAll(sortedVars[i].name, placeholder(i));
		}
		for (let i = 0; i < sortedVars.length; i++) {
			resolvedExpr = resolvedExpr.replaceAll(placeholder(i), sortedVars[i].value);
		}
		// 2. Try to evaluate as a math expression
		const result = EvaluateMathExpressionSafely(resolvedExpr);
		// 3. If a valid number, use it; otherwise fall back to the resolved expression
		if (!isNaN(result)) {
			// to avoid floating-point precision issues, round to 10 decimal places then convert to string
			// (example: 0.1 + 0.2 = 0.30000000000000004, but we want "0.3"; this line accomplishes that)
			return String(parseFloat(result.toFixed(10)));
		}
		return resolvedExpr;
	});
}

/** Master Ultimate Evaluation Function */
export function EvaluateMathExpressionSafely(expression) {
	// 1. Try JS first (Handles "10 + 5 - 2" instantly)
	try {
		return EvaluateMathExpressionViaJS(expression);
	} catch (e) {
		// 2. Fall back to CSS if JS fails (Handles complex configurations like "(10 - 2) + 5")
		try {
			return EvaluateMathExpressionViaCSS(expression);
		} catch (e2) {
			// Safe lock: If all options fail or syntax contains XSS strings, return NaN safely
			return NaN;
		}
	}
}

/** Tier 1: Pure JS Math Evaluation (Fastest)
 * Only accepts simple continuous tokens of numbers, +, and -. */
export function EvaluateMathExpressionViaJS(expression) {
	const safePattern = /^[0-9\s.+-]+$/;
	if (!safePattern.test(expression)) {
		throw new Error("JS parser rejected: Complex syntax detected.");
	}
	const tokens = expression.match(/[+-]?\s*[0-9.]+/g);
	if (!tokens) return 0;

	return tokens.reduce((total, token)=>{
		return total + parseFloat(token.replace(/\s+/g, ""));
	}, 0);
}

// Sentinel initial value: an obscure number unlikely to be produced by any real user expression.
const cssEvalSentinel = "98765.4";
if (typeof CSS !== "undefined" && CSS.registerProperty) {
	CSS.registerProperty({
		name: "--fallback-math-eval", syntax: "<number>", inherits: false,
		initialValue: cssEvalSentinel,
	});
}
/** Tier 2: CSS Houdini Math Evaluation (Medium) */
export function EvaluateMathExpressionViaCSS(expression) {
	if (!CSS.registerProperty) {
		throw new Error("CSS Houdini not supported in this browser.");
	}
	const helper = document.createElement("div");
	helper.style.setProperty("--fallback-math-eval", `calc(${expression})`);
	document.body.appendChild(helper);
	const parsed = parseFloat(getComputedStyle(helper).getPropertyValue("--fallback-math-eval").trim());
	helper.remove();

	if (isNaN(parsed) || parsed === parseFloat(cssEvalSentinel)) throw new Error("CSS failed to parse expression.");
	return parsed;
}